import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { StatusBadge } from "../StatusBadge";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { NoManuscript } from "./NoManuscript";

export function ComplianceList({ items, testId }) {
  const groups = ["Structure", "Dialogue Mechanics", "Sentence Flow", "Layout/Preview Compliance"];
  return (
    <div className="space-y-3" data-testid={testId}>
      {groups.filter((g) => items.some((i) => i.group === g)).map((g) => (
        <div key={g}><p className="text-xs font-mono text-amber-400/80 mb-1">[{g}]</p>
          {items.filter((i) => i.group === g).map((i, k) => <div key={k} className="flex justify-between gap-3 text-sm py-1"><span className="text-slate-300">{i.label}<span className="block text-xs text-slate-500">{i.detail}</span></span><StatusBadge status={i.status} /></div>)}
        </div>
      ))}
    </div>
  );
}

export default function StructureTab({ book, version, reload }) {
  const [meta, setMeta] = useState(null);
  const [s, setS] = useState(null);
  const [comp, setComp] = useState([]);
  useEffect(() => {
    api.get("/structure/frameworks").then((r) => setMeta(r.data));
    api.get(`/books/${book.id}/structure`).then((r) => { setS(r.data.structure || { framework: "three_act", beats: [], themes: "", prose_style: "novel" }); setComp(r.data.compliance); });
  }, [book.id]);
  if (!version) return <NoManuscript />;
  if (!meta || !s) return null;
  const fw = meta.frameworks[s.framework];
  const beat = (k) => s.beats.find((b) => b.key === k) || { key: k, chapter_ids: [], notes: "" };
  const setBeat = (k, patch) => setS({ ...s, beats: [...s.beats.filter((b) => b.key !== k), { ...beat(k), ...patch }] });
  const save = async () => { try { const { data } = await api.put(`/books/${book.id}/structure`, s); setComp(data.compliance); toast.success("Blueprint saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid xl:grid-cols-[1fr_380px] gap-6">
      <div className="space-y-4">
        <div className="panel p-5 flex flex-wrap gap-3 items-center" data-testid="structure-setup">
          <select value={s.framework} onChange={(e) => setS({ ...s, framework: e.target.value })} className="h-9 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm" data-testid="framework-select">
            {Object.entries(meta.frameworks).map(([k, f]) => <option key={k} value={k}>{f.name}</option>)}
          </select>
          <select value={s.prose_style} onChange={(e) => setS({ ...s, prose_style: e.target.value })} className="h-9 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm" data-testid="prose-style-select">
            {Object.entries(meta.styles).map(([k, f]) => <option key={k} value={k}>{f.name}</option>)}
          </select>
          <Button onClick={save} className="ml-auto bg-amber-600" data-testid="save-structure-button">Save blueprint</Button>
          <Textarea placeholder="Central themes (e.g. grief, duty, the sea as memory)" value={s.themes} onChange={(e) => setS({ ...s, themes: e.target.value })} className="min-h-[50px]" data-testid="themes-input" />
        </div>
        <div className="panel divide-y divide-slate-800" data-testid="beats-list">
          {fw.beats.map((b, i) => (
            <div key={b.key} className="p-4 grid md:grid-cols-[220px_1fr] gap-3" data-testid={`beat-${b.key}`}>
              <div><p className="text-sm text-slate-200">{i + 1}. {b.label}</p>{b.checkpoint && <StatusBadge status="WARNING" label="mandatory checkpoint" />}</div>
              <div className="space-y-2">
                <div className="max-h-28 overflow-y-auto scrollbar-thin border border-slate-800 rounded-md p-1" data-testid={`beat-chapters-${b.key}`}>
                  {version.chapters.map((c) => { const on = beat(b.key).chapter_ids.includes(c.id); return (
                    <label key={c.id} className="flex items-center gap-2 text-xs text-slate-300 px-1 py-0.5"><input type="checkbox" checked={on} onChange={() => setBeat(b.key, { chapter_ids: on ? beat(b.key).chapter_ids.filter((x) => x !== c.id) : [...beat(b.key).chapter_ids, c.id] })} data-testid={`beat-${b.key}-chapter-${c.id}`} />{c.title}</label>); })}
                </div>
                <Textarea placeholder="Notes" value={beat(b.key).notes} onChange={(e) => setBeat(b.key, { notes: e.target.value })} className="min-h-[40px] text-xs" data-testid={`beat-notes-${b.key}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="panel p-5 h-fit lg:sticky lg:top-6">
        <p className="eyebrow mb-3">Checkpoint compliance</p>
        {comp.length ? <ComplianceList items={comp} testId="structure-compliance" /> : <p className="text-sm text-slate-500">Save a blueprint to check checkpoints.</p>}
        <p className="text-[11px] text-slate-500 mt-4">Character arcs, world rules and canon live in the Continuity Manager and are fed into the writing assistant automatically.</p>
      </div>
    </div>
  );
}
